import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Modal, Select, Tooltip } from 'antd'
import { Button, Chevron, theme } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import useGenerate from 'screens/Rostering/hooks/useGenerate'
import DateRange from '../DateRange'
import { DAY_FIRST_DATE_FORMAT } from 'screens/Rostering/utils.datetime'
import { QuestionCircleOutlined } from '@ant-design/icons'
import useAllocationTemplateList from 'screens/Rostering/hooks/useAllocationTemplateList'
import { useState } from 'react'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import dayjs from 'dayjs'
import { commonPatterns } from 'utils/enhancedTestIds'

type PublishModalProps = {
  'data-testid'?: string
  open: boolean
  onClose: (event?: any) => any
  fromDate: string
  toDate: string
}

const StartRotaPublishModal = ({
  'data-testid': dataTestId,
  open,
  onClose,
  fromDate,
  toDate
}: PublishModalProps) => {
  const { t } = useTranslation()
  const baseId = dataTestId || 'start-rota-publish-modal'
  const { generateRota } = useGenerate({ startDate: fromDate })
  const sendEvent = useMixpanelEvent()

  const flags = useFeatureFlags({
    flagNames: ['rostering_allocation_template']
  })?.data

  const flagsRosteringAllocationTemplate =
    flags?.rostering_allocation_template?.enabled

  const [templateValue, setTemplateValue] = useState<string | undefined>(
    undefined
  )

  const { templateRotas } = useAllocationTemplateList()

  const templateAllocationMap = templateRotas.reduce(
    (map: Map<string, string>, templateRota: any) => {
      // eslint-disable-next-line no-underscore-dangle
      map.set(templateRota.templateAlias, templateRota._id)
      return map
    },
    new Map()
  )

  const transformedTemplateOptions = Array.from(
    templateAllocationMap,
    ([templateAlias]) => ({
      label: templateAlias,
      value: templateAlias
    })
  )

  const onGenerate = () => {
    generateRota({
      allocationTemplateName: templateValue,
      allocationTemplateId: templateAllocationMap.get(templateValue)
    })

    sendEvent(MixpanelEvents.START_ROTA_GENERATED, {
      clickedAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      rotaStartDate: fromDate,
      rotaEndDate: toDate,
      templateUsed: !!templateValue,
      templateName: templateValue || null,
      templateId: templateValue
        ? templateAllocationMap.get(templateValue)
        : null
    })

    onClose()
  }

  return (
    <Modal
      data-testid={commonPatterns.modal(baseId, 'container')}
      destroyOnClose
      width={470}
      title={<Title level={1}>{t('START_DRAFT_ROTA')}</Title>}
      footer={
        <ModalFooter data-testid={commonPatterns.modal(baseId, 'footer')}>
          <Button
            onClick={onClose}
            data-testid={commonPatterns.button(baseId, 'cancel')}
          >
            {t('CANCEL')}
          </Button>
          <Button
            onClick={onGenerate}
            variant="primary"
            data-testid={commonPatterns.button(baseId, 'start')}
          >
            {t('START_ROTA')}
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
    >
      <ModalDivider />
      <DateRange
        fromDate={fromDate}
        toDate={toDate}
        dateFormat={DAY_FIRST_DATE_FORMAT}
      />
      <Text>{t('GENERATE_VISITS_AND_SHIFTS')}</Text>
      <ModalDivider />

      {flagsRosteringAllocationTemplate &&
        transformedTemplateOptions.length > 0 && (
          <AllocationTemplateSection>
            <StyledLabel>
              <Text>{t('SELECT_ALLOCATION_TEMPLATE')}</Text>
              <Tooltip
                color={theme.color.neutral.color3}
                placement="bottom"
                title={
                  <TooltipContent>
                    {t('SELECT_ALLOCATION_TEMPLATE_TOOLTIP')}
                  </TooltipContent>
                }
                overlayInnerStyle={{ width: '400px', padding: '8px 16px' }}
              >
                <MoreInfoIcon />
              </Tooltip>
              <LightText>{t('OPTIONAL')}</LightText>
            </StyledLabel>
            <Select
              placeholder={<Text>{t('PLEASE_SELECT')}</Text>}
              data-testid={commonPatterns.input(baseId, 'template-picker')}
              suffixIcon={<Chevron direction="down" />}
              value={templateValue}
              onChange={(value) => setTemplateValue(value)}
              allowClear
            >
              {transformedTemplateOptions.map((option) => {
                const optionValue = option.value
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                return (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    data-testid={`${baseId}-template-option-${optionValue}`}
                  >
                    <Text>{option.label}</Text>
                  </Select.Option>
                )
              })}
            </Select>
            <ModalDivider />
          </AllocationTemplateSection>
        )}
    </Modal>
  )
}

export default StartRotaPublishModal

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-transform: none;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
`

const ModalDivider = styled(Divider)`
  margin: 24px 0px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`
const StyledLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const AllocationTemplateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const TooltipContent = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
const MoreInfoIcon = styled(QuestionCircleOutlined)`
  font-size: 16px;
  margin-left: 4px;

  &:hover {
    color: ${(props) => props.theme.color.status.primary};
  }
`
const LightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
